import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  Observable,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { OrganizationFormModel } from 'src/app/core/models/organization/organization-form.model';
import { Organization } from 'src/app/core/models/organization/organization.interface';
import { MinimalProfile } from 'src/app/core/models/user/profile';
import { EmployeeService } from 'src/app/core/services/employee.service';
import { OrganizationService } from 'src/app/core/services/organization.service';
import { EditableOrganization } from '../editable-organization';
import { AuthService } from 'src/app/core/services/auth.service';

@Component({
    selector: 'app-create-organization',
    templateUrl: './create-organization.component.html',
    styleUrl: './create-organization.component.scss',
    providers: [OrganizationFormModel],
    standalone: false
})
export class CreateOrganizationComponent
  extends EditableOrganization
  implements OnInit, OnDestroy
{
  formModel = inject(OrganizationFormModel);
  private readonly employeeService = inject(EmployeeService);
  private readonly organizationService = inject(OrganizationService);
  private readonly authService = inject(AuthService);

  @Input({ required: true })
  public set companyId(value: string) {
    this._companyId = value;
  }

  @Output() public cancelled = new EventEmitter<void>();
  @Output() public created = new EventEmitter<Organization>();

  public companyEmployees$ = new Observable<MinimalProfile[]>();
  public isSubmitting = false;

  public defaultResponsibleUser?: MinimalProfile;

  private _companyId = '';
  private unsubscribe$ = new Subject<void>();

  ngOnInit(): void {
    this.employeeService.setEmployeesUrl(this._companyId);
    this.organizationService.setOrganizationsUrl(this._companyId);

    this.companyEmployees$ = this.employeeService.getCompanyEmployeesLookup();

    this.companyEmployees$
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((x) => x.length > 0),
        tap((employees) => {
          const userData = this.authService.user$.getValue().data!;

          this.defaultResponsibleUser = employees.find(
            (e) => e.id === userData.id,
          );

          this.formModel.form.patchValue({
            responsibleUserId: this.defaultResponsibleUser?.id,
          });
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public onCancel(): void {
    this.cancelled.emit();
  }

  public submitForm(): void {
    if (this.formModel.form.invalid) {
      this.formModel.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;

    this.organizationService
      .createOrganization(this.formModel.form.getRawValue())
      .pipe(
        tap((organization) => this.created.emit(organization)),
        catchError(() => EMPTY),
        finalize(() => (this.isSubmitting = false)),
      )
      .subscribe();
  }
}
